// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { keepPreviousData, useQuery } from '@tanstack/react-query';

// The coins aggregation API (Postgres indexer + fullnode RPC) exposed via nginx.
export const COINS_API_URL = import.meta.env.VITE_COINS_API_URL || 'https://api.safecoin.dev/coins-api';

export interface CoinSummary {
	coinType: string;
	holders: number;
	objects: number;
	onchainBalance: string;
	isNative: boolean;
	name: string | null;
	symbol: string | null;
	decimals: number | null;
	description: string | null;
	iconUrl: string | null;
	metadataId: string | null;
	totalSupply: string | null;
	circulatingSupply: string | null;
	priceUsd: number | null;
	liquidityUsd: number | null;
	marketCapUsd: number | null;
	fdvUsd: number | null;
	// 24h trading volume in USD, and its percent change vs the prior 24h.
	volumeUsd24h: number | null;
	volumeChange24h: number | null;
	// Coin creation time (metadata publish), epoch milliseconds.
	createdAtMs: number | null;
}

export interface CoinsListResponse {
	updatedAt: string;
	count: number;
	coins: CoinSummary[];
}

export interface CoinHolder {
	owner: string;
	balance: string;
	objects: number;
}

// Signed percent price change over several trailing windows (null = no data).
export interface CoinPriceChange {
	m30: number | null;
	h1: number | null;
	h4: number | null;
	h24: number | null;
}

// Aggregated 24h trading activity (all counts/amounts null until backed).
export interface CoinTradingStats {
	txns: number | null;
	buys: number | null;
	sells: number | null;
	volumeUsd: number | null;
	buyVolumeUsd: number | null;
	sellVolumeUsd: number | null;
	makers: number | null;
	buyers: number | null;
	sellers: number | null;
}

// The detail endpoint returns the list summary plus detail-only extras.
export interface CoinDetail extends CoinSummary {
	creator: string | null;
	priceChange: CoinPriceChange | null;
	trading: CoinTradingStats | null;
}

export interface CoinDetailResponse {
	updatedAt: string;
	coin: CoinDetail;
}

export type PriceHistoryRange = '30d' | 'max';

export interface CoinPricePoint {
	// Epoch milliseconds for the sample, and its USD price.
	t: number;
	price: number;
}

export interface CoinPriceHistoryResponse {
	coinType: string;
	range: PriceHistoryRange;
	points: CoinPricePoint[];
}

export interface CoinTransaction {
	digest: string;
	// Transaction kind (e.g. "Programmable Tx") and the leading Move call, if any.
	kind: string;
	function: string | null;
	// How many commands/calls of that function the tx contains (badge next to func).
	functionCount: number | null;
	timestampMs: number | null;
	// Full 0x-prefixed hex so the UI can match well-known/system accounts.
	sender: string | null;
	// Total gas used, in SAFELET (base units, 9 decimals).
	gasUsedSafelet: string | null;
}

export interface CoinTransactionsResponse {
	coinType: string;
	total: number;
	limit: number;
	offset: number;
	transactions: CoinTransaction[];
}

export interface CoinHoldersResponse {
	updatedAt: string;
	coinType: string;
	total: number;
	limit: number;
	offset: number;
	holders: CoinHolder[];
}

async function fetchJson<T>(path: string): Promise<T> {
	const response = await fetch(`${COINS_API_URL}${path}`);
	if (!response.ok) {
		throw new Error(`Coins API request failed: ${response.status}`);
	}
	return (await response.json()) as T;
}

export function useCoinsList() {
	return useQuery({
		queryKey: ['coins', 'list'],
		queryFn: () => fetchJson<CoinsListResponse>('/coins'),
		refetchInterval: 30_000,
		staleTime: 15_000,
	});
}

export function useCoinDetail(coinType?: string) {
	return useQuery({
		queryKey: ['coins', 'detail', coinType],
		queryFn: () => fetchJson<CoinDetailResponse>(`/coins/${encodeURIComponent(coinType!)}`),
		enabled: !!coinType,
		refetchInterval: 30_000,
		staleTime: 15_000,
	});
}

export const HOLDERS_PAGE_SIZE = 50;

export function useCoinHolders(coinType: string | undefined, page: number) {
	return useQuery({
		queryKey: ['coins', 'holders', coinType, page],
		queryFn: () =>
			fetchJson<CoinHoldersResponse>(
				`/coins/${encodeURIComponent(coinType!)}/holders?limit=${HOLDERS_PAGE_SIZE}&offset=${
					page * HOLDERS_PAGE_SIZE
				}`,
			),
		enabled: !!coinType,
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});
}

export function useCoinPriceHistory(coinType: string | undefined, range: PriceHistoryRange) {
	return useQuery({
		queryKey: ['coins', 'price-history', coinType, range],
		queryFn: () =>
			fetchJson<CoinPriceHistoryResponse>(
				`/coins/${encodeURIComponent(coinType!)}/price-history?range=${range}`,
			),
		enabled: !!coinType,
		staleTime: 60_000,
	});
}

export const COIN_TX_PAGE_SIZE = 10;

export function useCoinTransactions(coinType: string | undefined, page: number, pageSize: number) {
	return useQuery({
		queryKey: ['coins', 'txs', coinType, page, pageSize],
		queryFn: () =>
			fetchJson<CoinTransactionsResponse>(
				`/coins/${encodeURIComponent(coinType!)}/txs?limit=${pageSize}&offset=${page * pageSize}`,
			),
		enabled: !!coinType,
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});
}
